import { useState, useCallback } from 'react';
import { GeoJsonLayer, BitmapLayer } from '@deck.gl/layers';
import { TileLayer } from '@deck.gl/geo-layers';
import { calculateBounds } from '../MapUtils';
import { toast } from "react-toastify";
import { getAccessToken } from '../LoginForm/Auth/auth';
import { showMapConfirmToast } from "../Toast/mapToast";

const baseUrl = process.env.REACT_APP_BASE_URL;

export const useLayerManagement = (
  fetchData,
  setViewState,
  handleMapClick,
  setHoverInfo
) => {
  const [wfsLayers, setWfsLayers] = useState([]);
  const [wmsLayers, setWmsLayers] = useState([]);

  const getNamespaceFromUrl = (url) => {
    const typeName = new URLSearchParams(new URL(url).search).get('typeName');
    return typeName?.split(':')[0] || '';
  };

  const getFullLayerNameFromUrl = (url) => {
    return new URLSearchParams(new URL(url).search).get('typeName');
  };

  const handleLayerSubmit = useCallback(async (url, typeName) => {
    const fetchedData = await fetchData(url);

    if (!fetchedData) {
      console.error('Failed to load data from URL');
      return;
    }

    const namespace = getNamespaceFromUrl(url);
    const newLayerId = `wfs-${typeName || getFullLayerNameFromUrl(url)}`;
    const layerExists = wfsLayers.some(layer => layer.id === newLayerId);

    setWfsLayers(prevLayers => {
      const filteredLayers = prevLayers.filter(layer => layer.id !== newLayerId);

      const newLayer = new GeoJsonLayer({
        id: newLayerId,
        data: fetchedData,
        source: "catalog",
        namespace,
        opacity: 1,
        pickable: true,
        stroked: true,
        filled: true,
        visible: true,
        pointType: 'circle',
        lineWidthMinPixels: 2,
        lineWidthScale: 2,
        getFillColor: [255, 0, 0, 100],
        getLineColor: [255, 0, 0, 200],
        getPointRadius: 0.05,
        getLineWidth: 2,
        onHover: info => setHoverInfo(info),
        onClick: handleMapClick,
        onError: error => console.error('GeoJsonLayer error:', error),
      });

      return [...filteredLayers, newLayer];
    });

    if (!layerExists && fetchedData.features?.length > 0) {
      const bounds = calculateBounds(fetchedData.features);

      const centerLon = (bounds.minLon + bounds.maxLon) / 2;
      const centerLat = (bounds.minLat + bounds.maxLat) / 2;
      const lonRange = bounds.maxLon - bounds.minLon;
      const latRange = bounds.maxLat - bounds.minLat;
      const zoom = Math.log2(360 / Math.max(lonRange, latRange));

      showMapConfirmToast({
        title: "Pozicioniranje mape",
        message: "Da li želite da se prikaz mape pozicionira prema novom sloju?",
        confirmText: "Da",
        cancelText: "Ne",
        variant: "info",
        onConfirm: () => {
          setViewState(prev => ({
            ...prev,
            longitude: centerLon,
            latitude: centerLat,
            zoom,
            pitch: 0,
            bearing: 0,
            transitionDuration: 1000,
          }));
        },
      });
    }
  }, [fetchData, setViewState, setHoverInfo, handleMapClick, wfsLayers]);

  const createWmsTileLayer = (wmsBaseUrl, layerName) => {
    const cleanBaseUrl = wmsBaseUrl.split("?")[0];
    const layerId = `wms-${layerName}`;

    return new TileLayer({
      id: layerId,
      data: cleanBaseUrl,
      minZoom: 0,
      maxZoom: 23,
      tileSize: 256,
      visible: true,
      source: "catalog",

      renderSubLayers: props => {
        if (!props.data) return null;

        const { bbox: { west, south, east, north } } = props.tile;

        return new BitmapLayer(props, {
          data: null,
          image: props.data,
          bounds: [west, south, east, north],
        });
      },

      getTileData: async ({ bbox }) => {
        const { west, south, east, north } = bbox;

        const params = new URLSearchParams({
          service: "WMS",
          version: "1.1.1",
          request: "GetMap",
          layers: layerName,
          styles: "",
          format: "image/png",
          transparent: "true",
          width: "256",
          height: "256",
          srs: "EPSG:4326",
          bbox: `${west},${south},${east},${north}`,
        });

        const tileUrl = `${cleanBaseUrl}?${params.toString()}`;
        console.log("WMS tile URL:", tileUrl);

        try {
          const token = getAccessToken();

          const response = await fetch(tileUrl, {
            headers: token
              ? { Authorization: `Bearer ${token}` }
              : {},
          });

          const contentType = response.headers.get("content-type");
          console.log("WMS status:", response.status, "content-type:", contentType);

          if (!response.ok || !contentType?.includes("image")) {
            const text = await response.text();
            console.error("WMS odgovor nije slika:", text);
            return null;
          }

          const blob = await response.blob();
          return URL.createObjectURL(blob);
        } catch (error) {
          console.error("Failed to fetch WMS tile:", error);
          return null;
        }
      },
    });
  };

  const submitWmsLayer = (wmsBaseUrl, layerName) => {
    if (!wmsBaseUrl || !layerName) {
      toast.warning("Unesite WMS URL i naziv sloja.");
      return;
    }

    const layerId = `wms-${layerName}`;

    setWmsLayers(prev => {
      const filtered = prev.filter(layer => layer.id !== layerId);
      return [...filtered, createWmsTileLayer(wmsBaseUrl, layerName)];
    });
  };

  const addLayerFromCatalog = (layer) => {
    if (layer.type === 'WMS') {
      const layerId = `wms-${layer.name}`;
      const exists = wmsLayers.some(l => l.id === layerId);

      if (exists) {
        toast.warning('WMS sloj je već dodat.');
        return;
      }

      const wmsUrl = `${baseUrl}/geoserver/wms`;

      setWmsLayers(prev => [
        ...prev,
        createWmsTileLayer(wmsUrl, layer.name),
      ]);

      return;
    }

    if (layer.type === 'WFS') {
      const layerId = `wfs-${layer.name}`;
      const exists = wfsLayers.some(l => l.id === layerId);

      if (exists) {
        toast.warning('WFS sloj je već dodat.');
        return;
      }

      const url =
        `${baseUrl}/geoserver/ows?service=WFS&version=1.0.0&request=GetFeature` +
        `&typeName=${layer.name}&outputFormat=application/json`;

      handleLayerSubmit(url, layer.name);
    }
  };

  return {
    wfsLayers,
    wmsLayers,
    handleLayerSubmit,
    submitWmsLayer,
    addLayerFromCatalog,
    setWfsLayers,
    setWmsLayers,
  };
};